
import { Component, OnInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { CallsService } from '../../calls.service';
import { ref, get } from 'firebase/database';
import { WakeLockService } from '../../wake-lock.service';
import { SessionService } from '../../session.service';
import { AuthService } from '../../auth.service';
import { ActivatedRoute } from '@angular/router';
import { Subscription, take, filter } from 'rxjs';

@Component({
  selector: 'app-monitor',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './monitor.html',
  styleUrl: './monitor.css'
})
export class MonitorComponent implements OnInit, OnDestroy {
  roomId: string | null = null;
  stream: MediaStream | null = null;
  batteryLevel: number | null = null;
  batteryCharging: boolean | null = null;
  batteryChargingTime: number | null = null;
  batteryDischargingTime: number | null = null;
  parentBatteryLevel: number | null = null;
  parentBatteryCharging: boolean | null = null;
  parentBatteryChargingTime: number | null = null;
  parentBatteryDischargingTime: number | null = null;
  status = 'Initializing...';
  isStreaming = false;
  isFullscreen = false;
  dimLevel = 0;
  volumeLevel = 50;
  borderColor = '#FF0073';
  showStopConfirm = false;
  showVolSlider = false;
  showDimSlider = false;

  // Mute UI state (source of truth is room_config)
  camAudioMuteUntil = 0;
  videoMuted = false;
  micSecondsLeft = 0;
  showMuteOptions = false;
  readonly muteDurations = [1, 3, 5, 10];
  private micCountdownInterval: any = null;

  // Torch (flashlight) UI state — source of truth is room_config.torchOn
  torchOn = false;
  showTorchConfirm = false;

  private dimWriteTimer: any = null;
  private volWriteTimer: any = null;
  private lastLocalDimWrite = 0;
  private lastLocalVolWrite = 0;

  private subs: Subscription[] = [];
  private fullscreenChangeHandler = () => {
    this.isFullscreen = !!document.fullscreenElement;
    this.cdr.detectChanges();
  };

  ownerEmail: string | null = null;
  config: any = {};

  // Audio Context for White Noise
  private audioCtx: AudioContext | null = null;
  private whiteNoiseNode: AudioBufferSourceNode | null = null;
  private whiteNoiseGain: GainNode | null = null;

  viewers: any[] = [];

  constructor(
    private callsService: CallsService,
    private sessionService: SessionService,
    private auth: AuthService,
    private route: ActivatedRoute,
    private cdr: ChangeDetectorRef,
    private wakeLock: WakeLockService,
    private router: Router
  ) { }

  async ngOnInit() {
    console.log('[Monitor:ngOnInit] Initializing MonitorComponent');
    try {
      console.log('[Monitor] Requesting WakeLock...');
      await this.wakeLock.requestLock();
      console.log('[Monitor] WakeLock Requested.');
    } catch (wlErr) {
      console.error('[Monitor] WakeLock Failed', wlErr);
    }

    try {
      console.log('[Monitor] Getting RoomID from Route...');
      this.roomId = this.route.snapshot.paramMap.get('roomId');
      console.log('[Monitor] RoomID:', this.roomId);
    } catch (routeErr) {
      console.error('[Monitor] Route Param Failed', routeErr);
    }

    document.addEventListener('fullscreenchange', this.fullscreenChangeHandler);
    this.enterFullscreen();

    if (this.roomId) {
      console.log('[Monitor] Subscribing to Auth User...');
      // Wait for a valid user (skip initial null)
      this.auth.user$.pipe(
        filter(u => !!u),
        take(1)
      ).subscribe(async u => {
        console.log('[Monitor] Auth User Received:', u?.uid);
        if (u && this.roomId) {
          // authSub.unsubscribe(); // Managed by take(1)

          // 1. Strict Verification (Room Existence & Role)
          // We must verify we are still assigned as 'camera' to this room.
          // This prevents using cached IDs for deleted rooms or revoked access.
          const roleSnap = await get(ref(this.auth.realtimeDb, `room_assignments/${this.roomId}/${u.uid}`));
          if (!roleSnap.exists() || roleSnap.val().role !== 'camera') {
            console.error('[Monitor] Invalid session or role. Redirecting...');
            this.sessionService.log(this.roomId, 'Monitor:ngOnInit', 'Validation Failed: Room missing or wrong role');
            this.sessionService.clearRoom(); // Clear invalid room
            this.router.navigate(['/']);
            return;
          }

          await this.callsService.initialize(this.roomId);

          // Persist Room
          this.sessionService.joinRoom(this.roomId);

          // Subscribe to Observables
          this.subs.push(
            this.callsService.localStream$.subscribe(s => {
              this.stream = s ? new MediaStream(s.getVideoTracks()) : null;
              this.cdr.detectChanges();
            }),
            this.callsService.status$.subscribe(s => {
              console.log(`[Monitor] Received Status: ${s}`);
              this.status = s;
              this.cdr.detectChanges();
            }),
            this.callsService.isStreaming$.subscribe(isStreaming => {
              this.isStreaming = isStreaming;
              this.cdr.detectChanges();
            }),
            this.callsService.metadata$.subscribe(meta => {
              if (meta && meta.ownerEmail) {
                this.ownerEmail = meta.ownerEmail;
                this.cdr.detectChanges();
              }
            }),
            this.callsService.config$.subscribe(config => {
              if (config) {
                this.config = config;
                this.applyConfig(config);
                this.cdr.detectChanges();
              }
            }),
            this.callsService.viewers$.subscribe(viewers => {
              this.viewers = viewers;
              const parent = viewers.find(v => v.role === 'owner' || v.role === 'parent');
              const pb = parent?.battery;
              this.parentBatteryLevel = pb?.level ?? null;
              this.parentBatteryCharging = typeof pb?.charging === 'boolean' ? pb.charging : null;
              this.parentBatteryChargingTime = typeof pb?.chargingTime === 'number' ? pb.chargingTime : null;
              this.parentBatteryDischargingTime = typeof pb?.dischargingTime === 'number' ? pb.dischargingTime : null;
              this.cdr.detectChanges();
            }),
            this.callsService.battery$.subscribe(b => {
              this.batteryLevel = b.level;
              this.batteryCharging = b.charging;
              this.batteryChargingTime = b.chargingTime;
              this.batteryDischargingTime = b.dischargingTime;
              this.cdr.detectChanges();
            }),
            // Fatal Error Handler
            this.callsService.fatalError$.subscribe(err => {
              if (err) {
                console.error('[Monitor] Fatal Error:', err);
                alert('Connection Lost: ' + err);
                this.sessionService.clearRoom();
                this.router.navigate(['/']);
              }
            })
          );

          // Start Battery Monitoring
          this.monitorBattery();
        }
      });
    }
  }

  applyConfig(config: any) {
    if (this.roomId) {
      this.sessionService.log(this.roomId, 'Monitor:applyConfig', `Color: ${config.color}, Noise: ${config.whiteNoise}`);
    }

    // 1. Border color
    if (config.color) {
      this.borderColor = config.color;
    }

    // 2. Volume (only if not recently set locally)
    if (config.volume !== undefined && Date.now() - this.lastLocalVolWrite > 1000) {
      this.volumeLevel = config.volume;
      if (this.whiteNoiseGain && this.audioCtx) {
        this.whiteNoiseGain.gain.setTargetAtTime(config.volume / 100 * 0.5, this.audioCtx.currentTime, 0.3);
      }
    }

    // 3. Brightness (only if not recently set locally).
    // Config stores brightness 0–100 (high = bright); local dimLevel is the
    // inverse (high = dim/dark) because it drives the black overlay opacity.
    if (config.brightness !== undefined && Date.now() - this.lastLocalDimWrite > 1000) {
      this.dimLevel = 100 - config.brightness;
    }

    // 4. White Noise
    this.handleWhiteNoise(config.whiteNoise);

    // 5. Mute state (cam audio / video). CallsService enforces track.enabled —
    // here we only mirror the fields for UI and drive the countdown display.
    this.camAudioMuteUntil = config.camAudioMuteUntil || 0;
    this.videoMuted = !!config.videoMuted;
    this.refreshMicCountdown();

    // 6. Torch (mirror only — CallsService enforces it on the track)
    this.torchOn = !!config.torchOn;

    // 7. Stopped By Owner
    if (config.stoppedByOwner) {
      this.status = 'Stopped by Parent';
      this.callsService.stopLocalStream();
      this.stream = null;
    }
  }

  get isMicMuted(): boolean {
    return this.camAudioMuteUntil > Date.now();
  }

  get micCountdownDisplay(): string {
    if (this.micSecondsLeft <= 0) return 'Muted';
    const m = Math.floor(this.micSecondsLeft / 60);
    const s = this.micSecondsLeft % 60;
    return m > 0 ? `${m}:${s.toString().padStart(2, '0')}` : `${s}s`;
  }

  toggleMicMute() {
    if (this.isMicMuted) {
      this.callsService.updateConfig({ camAudioMuteUntil: null });
      return;
    }
    this.showMuteOptions = true;
  }

  confirmMicMute(minutes: number) {
    this.showMuteOptions = false;
    const until = Date.now() + minutes * 60000;
    this.callsService.updateConfig({ camAudioMuteUntil: until });
  }

  toggleVideoMute() {
    this.callsService.updateConfig({ videoMuted: !this.videoMuted });
  }

  toggleTorch() {
    if (this.torchOn) {
      this.callsService.updateConfig({ torchOn: false });
      return;
    }
    this.showTorchConfirm = true;
  }

  confirmTorchOn() {
    this.showTorchConfirm = false;
    this.callsService.updateConfig({ torchOn: true });
  }

  private refreshMicCountdown() {
    if (this.micCountdownInterval) {
      clearInterval(this.micCountdownInterval);
      this.micCountdownInterval = null;
    }
    if (!this.isMicMuted) {
      this.micSecondsLeft = 0;
      return;
    }
    const update = () => {
      this.micSecondsLeft = Math.max(0, Math.ceil((this.camAudioMuteUntil - Date.now()) / 1000));
      if (this.micSecondsLeft <= 0 && this.micCountdownInterval) {
        clearInterval(this.micCountdownInterval);
        this.micCountdownInterval = null;
      }
      this.cdr.detectChanges();
    };
    update();
    this.micCountdownInterval = setInterval(update, 1000);
  }

  handleWhiteNoise(enabled: boolean) {
    if (enabled) {
      if (!this.audioCtx) {
        this.audioCtx = new (window.AudioContext || (window as any).webkitAudioContext)();
      }
      if (this.audioCtx.state === 'suspended') {
        this.audioCtx.resume();
      }

      if (!this.whiteNoiseNode) {
        const bufferSize = this.audioCtx.sampleRate * 2;
        const buffer = this.audioCtx.createBuffer(1, bufferSize, this.audioCtx.sampleRate);
        const data = buffer.getChannelData(0);
        for (let i = 0; i < bufferSize; i++) {
          data[i] = Math.random() * 2 - 1;
        }

        this.whiteNoiseNode = this.audioCtx.createBufferSource();
        this.whiteNoiseNode.buffer = buffer;
        this.whiteNoiseNode.loop = true;

        this.whiteNoiseGain = this.audioCtx.createGain();
        this.whiteNoiseGain.gain.value = 0.05; // Start low

        this.whiteNoiseNode.connect(this.whiteNoiseGain);
        this.whiteNoiseGain.connect(this.audioCtx.destination);
        this.whiteNoiseNode.start();
      }

      // Fade In
      if (this.whiteNoiseGain) {
        this.whiteNoiseGain.gain.setTargetAtTime(0.5, this.audioCtx.currentTime, 5); // Fade to 0.5 over 5s
      }

    } else {
      // Fade Out
      if (this.whiteNoiseGain && this.audioCtx) {
        this.whiteNoiseGain.gain.setTargetAtTime(0, this.audioCtx.currentTime, 2);
        setTimeout(() => {
          if (this.whiteNoiseNode) {
            this.whiteNoiseNode.stop();
            this.whiteNoiseNode.disconnect();
            this.whiteNoiseNode = null;
          }
        }, 2500);
      }
    }
  }

  facingMode: 'user' | 'environment' = 'environment';

  toggleCamera() {
    this.facingMode = this.facingMode === 'user' ? 'environment' : 'user';
    this.sessionService.log(this.roomId || 'unknown', 'Monitor:toggleCamera', `Switching to ${this.facingMode}`);
    this.callsService.switchCamera(this.facingMode).catch(err => {
      console.error('Failed to switch camera', err);
      this.sessionService.log(this.roomId || 'unknown', 'Monitor:toggleCamera', `Error: ${err.message}`);
    });
  }

  onDimChange(event: Event) {
    this.dimLevel = Number((event.target as HTMLInputElement).value);
    this.lastLocalDimWrite = Date.now();
    clearTimeout(this.dimWriteTimer);
    this.dimWriteTimer = setTimeout(() => {
      this.callsService.updateConfig({ brightness: 100 - this.dimLevel });
    }, 250);
  }

  onVolumeChange(event: Event) {
    this.volumeLevel = Number((event.target as HTMLInputElement).value);
    this.lastLocalVolWrite = Date.now();
    clearTimeout(this.volWriteTimer);
    this.volWriteTimer = setTimeout(() => {
      this.callsService.updateConfig({ volume: this.volumeLevel });
    }, 250);
  }

  toggleVolSlider() { this.showVolSlider = !this.showVolSlider; }
  toggleDimSlider() { this.showDimSlider = !this.showDimSlider; }

  confirmStop() {
    this.showStopConfirm = false;
    this.callsService.endSession().then(() => {
      this.sessionService.clearRoom();
      this.router.navigate(['/']);
    });
  }

  toggleFullscreen() {
    if (document.fullscreenElement) {
      document.exitFullscreen().catch(() => {});
    } else {
      document.documentElement.requestFullscreen()
        .catch(err => console.warn('[Monitor] Fullscreen request failed', err));
    }
  }

  private enterFullscreen() {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen()
        .catch(err => console.warn('[Monitor] Auto-fullscreen failed', err));
    }
  }

  ngOnDestroy() {
    console.log(`[Monitor:ngOnDestroy] Destroying MonitorComponent. Subs: ${this.subs.length}`);
    clearTimeout(this.dimWriteTimer);
    clearTimeout(this.volWriteTimer);
    if (this.micCountdownInterval) {
      clearInterval(this.micCountdownInterval);
      this.micCountdownInterval = null;
    }
    this.subs.forEach(s => s.unsubscribe());
    document.removeEventListener('fullscreenchange', this.fullscreenChangeHandler);
    if (document.fullscreenElement) {
      document.exitFullscreen().catch(() => {});
    }
    if (this.audioCtx) {
      this.audioCtx.close();
    }
    this.wakeLock.releaseLock();
    this.callsService.cleanup();
  }

  batteryIcon(level: number | null, charging: boolean | null): string {
    if (level === null) return 'battery_unknown';
    if (charging) return 'battery_charging_full';
    if (level > 80) return 'battery_full';
    if (level > 50) return 'battery_5_bar';
    if (level > 30) return 'battery_3_bar';
    return 'battery_alert';
  }

  batteryColorClass(level: number | null, charging: boolean | null): string {
    if (level === null) return 'battery-unknown';
    if (charging) return 'battery-charging';
    if (level <= 30) return 'battery-low';
    return 'battery-on';
  }

  batteryTimeLabel(
    level: number | null,
    charging: boolean | null,
    chargingTime: number | null,
    dischargingTime: number | null
  ): string {
    if (level === null) return '';
    if (charging) return '';
    const secs = dischargingTime;
    if (secs === null || !isFinite(secs) || secs <= 0) return '';
    const mins = Math.round(secs / 60);
    if (mins < 60) return `${mins}m`;
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    return m > 0 ? `${h}h ${m}m` : `${h}h`;
  }

  async monitorBattery() {
    if ('getBattery' in navigator) {
      try {
        const battery = await (navigator as any).getBattery();
        this.pushBattery(battery);

        battery.addEventListener('levelchange', () => this.pushBattery(battery));
        battery.addEventListener('chargingchange', () => this.pushBattery(battery));
        battery.addEventListener('chargingtimechange', () => this.pushBattery(battery));
        battery.addEventListener('dischargingtimechange', () => this.pushBattery(battery));
      } catch (err) {
        console.error('Battery API not supported', err);
      }
    }
  }

  private pushBattery(battery: any) {
    const level = Math.round(battery.level * 100);
    const charging = !!battery.charging;
    const chargingTime = isFinite(battery.chargingTime) ? battery.chargingTime : null;
    const dischargingTime = isFinite(battery.dischargingTime) ? battery.dischargingTime : null;
    this.callsService.reportBattery(level, charging, chargingTime, dischargingTime);
  }

}
